import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { BookingApi } from "../../API/Apis/BookingApi";
import { useTranslation } from "react-i18next";
import WebCheckInLayout from "./WebCheckInLayout";
import BookingGetters from "../../store/onlineBooking/getters";
import { IoInformationCircle } from "react-icons/io5";

type Props = {
  onNext?: (reservationData: any) => void;
  onManualEntry?: () => void;
  onShowModal?: (message: string) => void;
  progress: any;
  onBack: any;
  setIsLoading: any;
  resDateIsPassed: any;
  rezIsRental: any;
  isWebCheckInDone: any;
  lowQualityUploadedImage: any;
};

const UploadVoucher = ({
  onNext,
  onManualEntry,
  onShowModal,
  progress,
  onBack,
  setIsLoading,
  resDateIsPassed,
  rezIsRental,
  isWebCheckInDone,
  lowQualityUploadedImage,
}: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { t: translate } = useTranslation();
  const isMounted = useRef(true);
  const appSettings = BookingGetters.getAppSettings();
  const preventMultipleWebCheckin = appSettings?.preventMultipleWebCheckin;
  const webCheckinSecondaryColor = appSettings?.webCheckinSecondaryColor;
  const webCheckinPrimaryColor = appSettings?.webCheckinPrimaryColor;

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const isImageBlurry = (file: File, threshold = 100): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = () => {
        img.src = reader.result as string;
      };
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(false);
          return;
        }
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const gray: number[] = [];
        for (let i = 0; i < imageData.data.length; i += 4) {
          const r = imageData.data[i];
          const g = imageData.data[i + 1];
          const b = imageData.data[i + 2];
          gray.push(0.299 * r + 0.587 * g + 0.114 * b);
        }
        let laplacianSum = 0;
        let laplacianSqSum = 0;
        let count = 0;
        for (let y = 1; y < canvas.height - 1; y++) {
          for (let x = 1; x < canvas.width - 1; x++) {
            const i = y * canvas.width + x;
            const laplacian =
              -4 * gray[i] +
              gray[i - 1] +
              gray[i + 1] +
              gray[i - canvas.width] +
              gray[i + canvas.width];
            laplacianSum += laplacian;
            laplacianSqSum += laplacian * laplacian;
            count++;
          }
        }
        const mean = laplacianSum / count;
        const variance = laplacianSqSum / count - mean * mean;
        resolve(variance < threshold);
      };
      reader.readAsDataURL(file);
    });
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const extension = file.name.split(".").pop()?.toLowerCase();
      const allowedExtensions = ["pdf", "jpeg", "jpg", "png"];
      if (extension && allowedExtensions.includes(extension)) {
        setSelectedFile(file);
        handleUpload(file);
      } else {
        onShowModal?.(translate("File Not Supported."));
      }
    }
    event.target.value = "";
  };

  const handleUpload = async (file?: File) => {
    const uploadFile = file || selectedFile;
    if (!uploadFile) {
      onShowModal?.(translate("Please select a file first."));
      return;
    }

    if (uploadFile.type.startsWith("image/")) {
      const isBlurry = await isImageBlurry(uploadFile);
      if (isBlurry) {
        onShowModal?.(lowQualityUploadedImage);
        return;
      }
    }

    if (isMounted.current) setIsLoading(true);

    try {
      const response = await BookingApi.uploadVoucherAndGetResDetails(
        uploadFile
      );

      const resData = response?.data?.data?.response;
      const rezIsCancelled = resData?.[0]?.rezIsCancelled;
      if (rezIsCancelled === "true") {
        setIsLoading(false);
        onShowModal?.(
          translate(
            "Web check-in is unavailable because this reservation has been cancelled."
          )
        );
        return;
      } else {
        const apiData = response?.data?.data;
        const apiDatastatus = response?.data?.status;
        const apiDatamsg = response?.data?.msg;

        if (apiDatastatus === "error" || apiData?.status === "error") {
          setIsLoading(false);
          if (
            apiDatamsg ===
              "The reservation has already been converted into a rental" ||
            apiData?.msg ===
              "The reservation has already been converted into a rental"
          ) {
            onShowModal?.(rezIsRental);
            return;
          }
          onShowModal?.(
            translate("Booking not found.\nPlease verify your details.")
          );
          return;
        }

        const pickUpDate = apiData?.response?.[0]?.pickUpDate;
        const pickUpTime = apiData?.response?.[0]?.pickUpTime;
        const reservationDateTime = new Date(`${pickUpDate}T${pickUpTime}`);
        const now = new Date();

        // default buffer = 1 hour
        const bufferHours = appSettings?.additionBufferHourForWebCheckin
          ? Number(appSettings.additionBufferHourForWebCheckin)
          : 1;
        const reservationDateTimeWithBuffer = new Date(
          reservationDateTime.getTime() + bufferHours * 60 * 60 * 1000
        );
        if (reservationDateTimeWithBuffer < now) {
          setIsLoading(false);
          onShowModal?.(resDateIsPassed);
          return;
        }

        if (
          apiData?.response?.[0]?.isWebCheckInDone === "true" &&
          preventMultipleWebCheckin
        ) {
          setIsLoading(false);
          onShowModal?.(isWebCheckInDone);
          return;
        }

        if (onNext && isMounted.current && apiDatastatus === "success") {
          await onNext(apiData.response[0]);
        } else {
          setIsLoading(false);
          onShowModal?.(
            translate("Booking not found.\nPlease verify your details.")
          );
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      setIsLoading(false);
    }
  };

  const webCheckinButtonColor = appSettings?.webCheckinButtonColor;
  const webCheckinButtonTextColor = appSettings?.webCheckinButtonTextColor;
  const IoInformation = IoInformationCircle as React.ElementType;

  return (
    <WebCheckInLayout
      title={translate("WEB CHECK IN")}
      progress={progress}
      onBack={onBack}
    >
      <Typography
        variant="subtitle1"
        sx={{
          textAlign: "center",
          mb: 2,
          fontSize: "1.2rem",
          fontWeight: "bold",
          fontFamily: "Inter, sans-serif",
          color: "#19344F",
        }}
      >
        {translate("Upload your Voucher")}
      </Typography>

      <Box sx={{ display: "flex", px: 3, alignItems: "flex-start" }}>
        <IoInformation
          style={{
            width: 20,
            height: 20,
            color: webCheckinPrimaryColor
              ? webCheckinPrimaryColor
              : webCheckinSecondaryColor || "#E3007B",
            flexShrink: 0,
            lineHeight: 1,
            marginLeft: "20px",
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: "#868686",
            fontFamily: "Inter, sans-serif",
            textAlign: "center",
            lineHeight: "111%",
            letterSpacing: "0%",
          }}
        >
          {translate(
            "You can find your voucher reservation in your Email.Please Upload here."
          )}
        </Typography>
      </Box>

      <Box sx={{ mt: 4, mb: 4, display: "flex", justifyContent: "center" }}>
        <input
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: "none" }}
          id="voucher-upload"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="voucher-upload">
          <Button
            variant="contained"
            component="span"
            sx={{
              backgroundColor: webCheckinButtonColor || "#19344F",
              color: webCheckinButtonTextColor || "#FFFFFF",
              borderRadius: `${appSettings?.buttonBorderRadius || 30}px`,
              px: 3.2,
              py: 2.3,
              fontWeight: "bold",
              fontFamily: "Inter, sans-serif",
              "&:hover": {
                backgroundColor: webCheckinButtonColor || "#19344F",
                color: webCheckinButtonTextColor || "#FFFFFF",
              },
            }}
          >
            <img
              src="/assets/icons/upload.png"
              alt="upload"
              style={{
                width: 20,
                height: 20,
                marginRight: 10,
                color: webCheckinButtonTextColor || "#fff",
              }}
              loading="lazy"
              decoding="async"
            />
            {translate("UPLOAD")}
          </Button>
        </label>
      </Box>

      <Box className="mt-4">
        <Typography
          variant="caption"
          sx={{
            color: webCheckinSecondaryColor
              ? webCheckinSecondaryColor
              : "#E3007B",
            fontFamily: "Inter, sans-serif",
            fontSize: "1rem",
            fontWeight: "bold",
            cursor: "pointer",
            textAlign: "center",
            display: "block",
            lineHeight: "111%",
            letterSpacing: "0%",
            mt: 2,
            mb: 2,
            textDecoration: "underline",
          }}
          onClick={() => onManualEntry && onManualEntry()}
        >
          {translate("Or enter your booking details manually.")}
        </Typography>
      </Box>
    </WebCheckInLayout>
  );
};

export default UploadVoucher;
